import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import FooterSection from "@/pages/home/components/FooterSection";

const SITE_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://mulebuy.asia';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | Mulebuy Spreadsheet`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", article.excerpt);
      }
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute("content", article.title);
      }
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) {
        ogDesc.setAttribute("content", article.excerpt);
      }
      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) {
        ogUrl.setAttribute("content", articleUrl);
      }
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", articleUrl);
    }
    return () => {
      document.title = "Best Mulebuy Spreadsheet 2026 | Trusted Sellers & Latest Finds";
      const restoreMetaDesc = document.querySelector('meta[name="description"]');
      if (restoreMetaDesc) {
        restoreMetaDesc.setAttribute("content", "Explore the latest Mulebuy Spreadsheet with trusted sellers, QC guides, shipping tips, verified products, and updated finds for sneakers, clothing, bags, and more.");
      }
      const restoreOgUrl = document.querySelector('meta[property="og:url"]');
      if (restoreOgUrl) {
        restoreOgUrl.setAttribute("content", SITE_URL);
      }
      const canonicalEl = document.querySelector('link[rel="canonical"]');
      if (canonicalEl) {
        canonicalEl.setAttribute("href", SITE_URL);
      }
    };
  }, [article, articleUrl]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">Article Not Found</h1>
          <p className="mt-3 text-foreground-500 text-sm">The article you are looking for does not exist.</p>
          <Link
            to="/"
            className="whitespace-nowrap cursor-pointer mt-5 inline-flex items-center gap-2 bg-primary-500 text-white px-5 py-2.5 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors duration-200"
          >
            <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const articleUrl = `${SITE_URL}/article/${article.slug}`;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Article Header */}
        <section className="pt-20 pb-10 md:pt-24 md:pb-14 bg-background-100 border-b border-background-200">
          <div className="container-site">
            <Link
              to="/"
              onClick={() => window.scrollTo(0, 0)}
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 text-foreground-500 text-sm hover:text-accent-600 transition-colors duration-200 mb-5"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Back to Mulebuy Spreadsheet Home
            </Link>

            <p className="text-accent-600 text-sm font-medium tracking-wider uppercase mb-2">/ Article</p>

            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight max-w-4xl">
              {article.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                Updated June 22, 2026
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                Mulebuy Editorial Team
              </span>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {["Mulebuy Spreadsheet", "Mulebuy Sellers", "Mulebuy Finds", "Mulebuy 2026"].map((tag) => (
                <span
                  key={tag}
                  className="whitespace-nowrap inline-flex items-center bg-accent-50 text-accent-800 text-xs font-medium px-3 py-1 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="bg-background-50">
          <div className="container-site py-8 md:py-10">
            <div className="rounded-2xl overflow-hidden border border-background-200">
              <img
                src={article.thumbnailUrl}
                alt={article.title}
                title={`${article.title} - Mulebuy Spreadsheet`}
                className="w-full h-[250px] md:h-[400px] object-cover"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="pb-14 md:pb-20 bg-background-50">
          <div className="container-site">
            <div className="max-w-3xl mx-auto">
              <div className="bg-white border border-background-200 rounded-2xl p-6 md:p-10">
                {article.sections.map((section, i) => (
                  <div
                    key={i}
                    id={`section-${i}`}
                    className={i > 0 ? "mt-10 pt-8 border-t border-background-100" : ""}
                  >
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                      {section.title}
                    </h2>
                    <div className="mt-4 space-y-4">
                      {section.content.split("\n\n").map((paragraph, j) => (
                        <p key={j} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Back to Home CTA */}
                <div className="mt-14 pt-8 border-t border-background-200 text-center">
                  <h3 className="text-xl font-heading font-bold text-foreground-950">
                    Explore More Mulebuy Spreadsheet Resources
                  </h3>
                  <p className="mt-2 text-sm text-foreground-500">
                    Return to the main Mulebuy Spreadsheet hub to browse all categories, read more guides, and access the latest updates.
                  </p>
                  <Link
                    to="/"
                    onClick={() => window.scrollTo(0, 0)}
                    className="whitespace-nowrap cursor-pointer mt-4 inline-flex items-center gap-2 bg-primary-500 text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-primary-600 transition-colors duration-200"
                  >
                    <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
                    Back to Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <FooterSection />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "url": articleUrl,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": articleUrl,
            },
            "author": {
              "@type": "Organization",
              "name": "Mulebuy Editorial Team",
            },
            "datePublished": "2026-06-22",
            "dateModified": "2026-06-22",
            "publisher": {
              "@type": "Organization",
              "name": "Mulebuy.asia",
              "url": SITE_URL,
            },
            "image": article.thumbnailUrl,
          }),
        }}
      />
    </div>
  );
}